import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  AlertTriangle, ArrowLeft, CheckCircle2, Copy, FolderKanban, HelpCircle, ListChecks, Lock, Loader2, Plus, RotateCcw,
  Search, Sparkles, Trash2, X,
} from 'lucide-react';
import { apiService } from '../../services/api';
import { useAuth } from '../Auth';
import { formatDuration, formatMeetingDate, speakerLabel, statusOf } from './meetingStatus';
import { formatClock } from './RecorderBar';
import { useMeetingRecorder } from './MeetingRecorderContext';
import MeetingActions from './MeetingActions';
import MeetingParticipants from './MeetingParticipants';
import NewProjectProposal from './NewProjectProposal';
import ScopePicker, { SCOPE_KINDS, scopeFields, scopeKey, scopeLabel } from './ScopePicker';
import VoiceFromMeeting from './VoiceFromMeeting';

// Réunion sans portée : la rattacher à un projet, un client ou un programme (sa synthèse rejoint alors le journal).
// Mauvaise portée : la déplacer (journaux, objectifs acceptés et carnets suivent ; synthèse refaite).
const ChangeScope = ({ meeting, onChanged }) => {
  const [open, setOpen] = useState(false);
  const [error, setError] = useState(null);
  const current = meeting.scope;
  const several = current && current.kind !== 'project';
  const attach = async (scope) => {
    setError(null);
    try {
      onChanged(await apiService.patch(`/meetings/${meeting.id}`, scopeFields(scope)));
      setOpen(false);
    } catch (err) {
      setError(err.message);
    }
  };
  const Icon = current ? SCOPE_KINDS[current.kind].icon : null;
  const note = !current
    ? "Projet : la synthèse rejoint son journal et son équipe voit la réunion. Client ou programme : Nexus refait la synthèse et dit ce qui revient à chacun de ses projets."
    : several
      ? 'Les parts publiées dans les journaux des projets s’en vont ; la synthèse est refaite avec la nouvelle portée (quelques minutes). Les objectifs déjà créés restent à leur projet.'
      : `Tout quitte « ${current.name} » : entrée de journal, objectifs acceptés (vers un autre projet), notes du carnet. La synthèse est refaite avec la nouvelle portée (quelques minutes).`;
  return (
    <span className="relative flex flex-wrap items-center gap-2">
      {several && (
        <span className={`flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold ${SCOPE_KINDS[current.kind].chip}`}>
          <Icon className="h-3.5 w-3.5" />{scopeLabel(current)}
        </span>
      )}
      {current ? (
        meeting.can_read_transcript && meeting.status !== 'summarizing' && (
          <button type="button" onClick={() => setOpen((v) => !v)} className="rounded-full px-2 py-0.5 text-xs text-slate-400 hover:bg-slate-100 hover:text-slate-700">
            Changer de {current.kind === 'project' ? 'projet' : current.kind}
          </button>
        )
      ) : (
        <button type="button" onClick={() => setOpen((v) => !v)} className="rounded-full bg-violet-50 px-2.5 py-0.5 text-xs font-semibold text-violet-700 hover:bg-violet-100">
          Sans projet · rattacher à un projet, un client…
        </button>
      )}
      {open && (
        <span className="absolute left-0 top-full z-20 mt-2 block w-80 rounded-xl border border-slate-200 bg-white p-2 shadow-lg">
          <ScopePicker onPick={attach} exclude={current ? [scopeKey(current)] : []} />
          <span className="mt-1 block px-1 text-[11px] text-slate-400">{note}</span>
          {error && <span className="block px-1 text-xs text-rose-600">{error}</span>}
        </span>
      )}
    </span>
  );
};

const SOURCE_LABELS = { nexus: "d'après Nexus", manual: 'ajouté', action: 'par une action' };

// Réunion d'un client ou d'un programme : les projets qu'elle concerne et ce qui revient à chacun (publié dans leur
// journal). Un projet se retire (sa part quitte son journal) ou s'ajoute (il reçoit la synthèse).
const ConcernedProjects = ({ meeting, onChange }) => {
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  if (!meeting.scope || meeting.scope.kind === 'project') return null;
  const concerned = meeting.projects || [];
  const change = async (body, key) => {
    setBusy(key);
    setError(null);
    try {
      onChange(await apiService.put(`/meetings/${meeting.id}/projects`, body));
      setAdding(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(null);
    }
  };
  const canEdit = meeting.can_edit && meeting.status !== 'summarizing';
  const candidates = (meeting.scope.projects || []).filter((p) => !concerned.some((c) => c.project_id === p.id))
    .map((p) => ({ kind: 'project', id: p.id, name: p.name, reason: `projet ${p.status === 'proposition' ? 'proposé' : 'en cours'} ${meeting.scope.kind === 'client' ? 'du client' : 'du programme'}` }));
  return (
    <div>
      <h3 className="mb-1.5 flex items-center gap-1.5 text-sm font-semibold text-slate-900">
        <FolderKanban className="h-4 w-4 text-slate-400" /> Ce qui revient à chaque projet
      </h3>
      {!concerned.length && (
        <p className="text-sm text-slate-500">
          {['transcribed', 'summarizing'].includes(meeting.status) || statusOf(meeting.status).busy
            ? 'Nexus dira quels projets la réunion concerne en rédigeant la synthèse.'
            : 'Aucun projet désigné par Nexus : ajoute ceux que la réunion concerne.'}
        </p>
      )}
      <div className="space-y-2">
        {concerned.map((part) => (
          <div key={part.project_id} className="rounded-xl border border-slate-200 px-3 py-2">
            <div className="flex items-center justify-between gap-2">
              <Link to={`/project/${part.project_id}`} className="truncate text-sm font-semibold text-slate-900 hover:text-blue-600">{part.name}</Link>
              <span className="flex shrink-0 items-center gap-2 text-[11px] text-slate-400">
                {SOURCE_LABELS[part.source] || part.source}{part.journal_entry_id ? ' · au journal' : ''}
                {canEdit && (
                  <button type="button" onClick={() => change({ remove: [part.project_id] }, part.project_id)} disabled={Boolean(busy)}
                    title="Retirer : sa part quitte le journal du projet, Nexus ne le remettra pas" className="rounded p-0.5 hover:bg-slate-100 hover:text-rose-600">
                    {busy === part.project_id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <X className="h-3.5 w-3.5" />}
                  </button>
                )}
              </span>
            </div>
            {part.summary && <p className="mt-1 text-sm text-slate-700">{part.summary}</p>}
            {part.points?.length > 0 && (
              <ul className="mt-1 list-disc space-y-0.5 pl-5 text-sm text-slate-700">{part.points.map((point, i) => <li key={i}>{point}</li>)}</ul>
            )}
            {!part.summary && !part.points?.length && (
              <p className="mt-1 text-xs text-slate-400">Pas de part écrite par Nexus : la synthèse entière est dans son journal.</p>
            )}
          </div>
        ))}
      </div>
      {canEdit && (adding ? (
        <div className="mt-2 rounded-xl border border-slate-200 p-2">
          <ScopePicker kinds={['project']} suggestions={candidates} exclude={concerned.map((c) => `project:${c.project_id}`)}
            onPick={(scope) => change({ add: [scope.id] }, 'add')} placeholder="Chercher un projet…" />
          <button type="button" onClick={() => setAdding(false)} className="mt-1 px-1 text-xs text-slate-400 hover:text-slate-600">Annuler</button>
        </div>
      ) : (
        <button type="button" onClick={() => setAdding(true)} className="mt-2 flex items-center gap-1 text-xs text-blue-600 hover:underline">
          <Plus className="h-3.5 w-3.5" /> Ajouter un projet concerné
        </button>
      ))}
      {error && <p className="mt-1 text-xs text-rose-600">{error}</p>}
    </div>
  );
};

const POLL_MS = 10000;
const LANGUAGES = { fr: 'en français', en: 'en anglais' };
const SPEAKER_COLORS = ['text-blue-700', 'text-emerald-700', 'text-violet-700', 'text-amber-700', 'text-rose-700',
  'text-cyan-700', 'text-lime-700', 'text-fuchsia-700'];
const STEPS = [
  { key: 'upload', label: 'Envoi', statuses: ['recording'] },
  { key: 'transcribe', label: 'Transcription', statuses: ['queued', 'transcribing'] },
  { key: 'summary', label: 'Synthèse', statuses: ['transcribed', 'summarizing'] },
  { key: 'ready', label: 'Prête', statuses: ['ready'] },
];

const speakerColor = (speaker) => {
  const match = /^speaker_(\d+)$/.exec(speaker || '');
  return match ? SPEAKER_COLORS[Number(match[1]) % SPEAKER_COLORS.length] : 'text-slate-400';
};

const Progress = ({ status }) => {
  const current = STEPS.findIndex((step) => step.statuses.includes(status));
  return (
    <ol className="flex items-center gap-2 text-xs">
      {STEPS.map((step, index) => (
        <li key={step.key} className="flex items-center gap-2">
          <span className={`flex items-center gap-1 rounded-full px-2 py-1 font-semibold ${
            index < current ? 'bg-emerald-50 text-emerald-700' : index === current ? 'bg-blue-50 text-blue-700' : 'bg-slate-50 text-slate-400'}`}>
            {index < current && <CheckCircle2 className="h-3.5 w-3.5" />}
            {index === current && status !== 'ready' && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            {step.label}
          </span>
          {index < STEPS.length - 1 && <span className="h-px w-4 bg-slate-200" />}
        </li>
      ))}
    </ol>
  );
};

const Section = ({ icon: Icon, title, items }) => (items?.length ? (
  <div>
    <h3 className="mb-1.5 flex items-center gap-1.5 text-sm font-semibold text-slate-900"><Icon className="h-4 w-4 text-slate-400" />{title}</h3>
    <ul className="list-disc space-y-1 pl-5 text-sm text-slate-700">{items.map((item, i) => <li key={i}>{item}</li>)}</ul>
  </div>
) : null);

// Notes détaillées des longues réunions (Markdown simple : « ### hh:mm — sujet » puis des puces), repliées.
const DetailedNotes = ({ notes }) => {
  const [open, setOpen] = useState(false);
  if (!notes) return null;
  if (notes.status !== 'ready' || !notes.markdown) {
    return notes.status === 'writing' ? (
      <p className="flex items-center gap-2 text-xs text-slate-400"><Loader2 className="h-3 w-3 animate-spin" />Nexus écrit les notes détaillées de la réunion…</p>
    ) : null;
  }
  const blocks = notes.markdown.split(/\n(?=### )/).map((block) => {
    const [head, ...lines] = block.trim().split('\n');
    return { title: head.replace(/^###\s*/, ''), items: lines.map((l) => l.replace(/^\s*-\s*/, '').trim()).filter(Boolean) };
  });
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50/60">
      <button type="button" onClick={() => setOpen((v) => !v)} className="flex w-full items-center justify-between px-3 py-2 text-left text-sm font-semibold text-slate-800">
        <span>Notes détaillées <span className="font-normal text-slate-400">· {blocks.length} sujet{blocks.length > 1 ? 's' : ''}, tout ce que la synthèse laisse de côté</span></span>
        <span className="text-xs text-slate-400">{open ? 'Replier' : 'Déplier'}</span>
      </button>
      {open && (
        <div className="space-y-3 border-t border-slate-200 px-3 py-3">
          {blocks.map((block, i) => (
            <div key={i}>
              <h4 className="mb-1 text-sm font-semibold text-slate-900">{block.title}</h4>
              <ul className="list-disc space-y-0.5 pl-5 text-sm text-slate-700">{block.items.map((item, j) => <li key={j}>{item}</li>)}</ul>
            </div>
          ))}
          {notes.stale && <p className="text-xs text-amber-700">La synthèse a été refaite : ces notes seront bientôt réécrites.</p>}
        </div>
      )}
    </div>
  );
};

const KIND_LABELS = { participant: 'participant', projet: 'équipe du projet', invité: 'invité' };

const Transcript = ({ meeting, onRename }) => {
  const [query, setQuery] = useState('');
  const names = meeting.speaker_names || {};
  const candidates = meeting.speaker_candidates || [];
  const listId = `speaker-candidates-${meeting.id}`;
  const linkedName = (speaker) => {
    const employeeId = meeting.speaker_sources?.[speaker]?.employee_id;
    return employeeId ? candidates.find((c) => c.employee_id === employeeId)?.name || names[speaker] : null;
  };
  const segments = useMemo(() => meeting.transcript?.segments || [], [meeting.transcript]);
  const speakers = useMemo(() => [...new Set(segments.map((s) => s.speaker).filter(Boolean))].sort(), [segments]);
  const visible = query
    ? segments.filter((s) => s.text.toLowerCase().includes(query.toLowerCase()))
    : segments;

  const copy = () => navigator.clipboard?.writeText(segments
    .map((s) => `[${formatClock(s.start)}] ${speakerLabel(s.speaker, names)} : ${s.text}`).join('\n'));

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <datalist id={listId}>
          {candidates.map((c) => (
            <option key={`${c.employee_id || c.name}`} value={c.name}>
              {[KIND_LABELS[c.kind] || c.kind, c.detail].filter(Boolean).join(' · ')}
            </option>
          ))}
        </datalist>
        {speakers.map((speaker) => (
          <label key={speaker} className="flex items-center gap-1.5 rounded-full border border-slate-200 py-0.5 pl-2.5 pr-1 text-xs">
            <span className={`font-semibold ${speakerColor(speaker)}`}>{speakerLabel(speaker)}</span>
            {meeting.speaker_sources?.[speaker]?.source === 'voice' && (
              <span className="text-[10px]" title={`Reconnu à la voix (similarité ${meeting.speaker_sources[speaker].similarity})`}>🎙</span>
            )}
            {meeting.speaker_sources?.[speaker]?.source !== 'voice' && meeting.voice_diagnostics?.[speaker] && (
              <span className="cursor-help text-[10px] text-slate-400" title={`Voix non reconnue : ${meeting.voice_diagnostics[speaker].reason}${
                (meeting.voice_diagnostics[speaker].candidates || []).length
                  ? ` (${meeting.voice_diagnostics[speaker].candidates.map((c) => `${c.name} ${c.similarity}`).join(', ')})` : ''}`}>🎙?</span>
            )}
            <span className="text-slate-300">→</span>
            <input
              key={names[speaker] || ''}
              defaultValue={names[speaker] || ''}
              placeholder="Nom"
              list={listId}
              onBlur={(e) => {
                const value = e.target.value.trim();
                if ((names[speaker] || '') === value) return;
                // Choisi dans les suggestions : relié à l'employé (qui peut alors lire le transcript).
                const picked = candidates.find((c) => c.employee_id && c.name.toLowerCase() === value.toLowerCase());
                onRename(speaker, value, picked ? picked.employee_id : undefined);
              }}
              onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
              className="w-28 rounded-full px-1.5 py-0.5 text-xs outline-none focus:bg-slate-50"
            />
            {linkedName(speaker) && (
              <span className="text-[10px]" title={`Relié à ${linkedName(speaker)} : participant, il peut lire le transcript`}>👤</span>
            )}
          </label>
        ))}
        <div className="ml-auto flex items-center gap-2">
          <div className="flex items-center gap-1.5 rounded-lg border border-slate-200 px-2 py-1">
            <Search className="h-3.5 w-3.5 text-slate-400" />
            <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Chercher" className="w-28 text-xs outline-none" />
          </div>
          <button type="button" onClick={copy} className="rounded-lg border border-slate-200 p-1.5 text-slate-500 hover:bg-slate-50" title="Copier le transcript">
            <Copy className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
      <div className="space-y-2.5">
        {visible.map((segment, index) => (
          <div key={index} className="grid grid-cols-[3.5rem_1fr] gap-2 text-sm">
            <span className="pt-0.5 font-mono text-[11px] text-slate-400">{formatClock(segment.start)}</span>
            <p className="text-slate-700">
              <span className={`mr-1.5 font-semibold ${speakerColor(segment.speaker)}`}>{speakerLabel(segment.speaker, names)}</span>
              {segment.overlap && <span className="mr-1 text-[10px] uppercase text-amber-600">chevauchement</span>}
              {segment.text}
            </p>
          </div>
        ))}
        {!visible.length && <p className="text-sm text-slate-400">Rien ne correspond.</p>}
      </div>
    </div>
  );
};

// Plus aucun son depuis 2 minutes (onglet fermé, page rechargée, session expirée) : l'auteur envoie ce qui est arrivé
// sans attendre la reprise automatique, une heure après le dernier son (INTERRUPTED_AFTER côté serveur).
const INTERRUPTED_AFTER_MS = 2 * 60 * 1000;

const InterruptedRecording = ({ meeting, onSent }) => {
  const recorder = useMeetingRecorder();
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const last = Date.parse(meeting.last_chunk_at || meeting.started_at);
  const recordingHere = recorder.meeting?.id === meeting.id && ['recording', 'sending', 'error'].includes(recorder.phase);
  if (recordingHere || !(Date.now() - last > INTERRUPTED_AFTER_MS)) return null;
  const send = async () => {
    setSending(true);
    setError(null);
    try {
      onSent(await apiService.post(`/meetings/${meeting.id}/close`, {}));
    } catch (err) {
      setError(err.message);
    } finally {
      setSending(false);
    }
  };
  return (
    <div className="mt-3 flex flex-wrap items-center gap-3 rounded-xl bg-amber-50 p-3 text-sm text-amber-900">
      <AlertTriangle className="h-4 w-4" />
      <span className="flex-1">
        L’enregistrement s’est arrêté à {new Date(last).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })} sans
        être terminé (onglet fermé, session expirée…). L’audio reçu jusque-là est gardé ; sinon il part tout seul une heure après.
        {error && <span className="block text-xs text-rose-700">{error}</span>}
      </span>
      <button type="button" onClick={send} disabled={sending}
        className="flex items-center gap-1 rounded-lg bg-amber-600 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-60">
        {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />} Envoyer l’audio reçu
      </button>
    </div>
  );
};

const MeetingDetail = () => {
  const { meetingId } = useParams();
  const navigate = useNavigate();
  const [meeting, setMeeting] = useState(null);
  const [error, setError] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [employees, setEmployees] = useState([]);
  const { user } = useAuth();
  const [revealed, setRevealed] = useState(null); // transcript masqué, lu par un administrateur (débogage)

  useEffect(() => {
    apiService.get('/employees').then((rows) => setEmployees((rows || []).filter((e) => e.is_active))).catch(() => {});
  }, []);

  const load = useCallback(() => apiService.get(`/meetings/${meetingId}`)
    .then((data) => { setMeeting(data); setError(null); })
    .catch((err) => setError(err.message)), [meetingId]);

  useEffect(() => { load(); }, [load]);

  const busy = meeting && statusOf(meeting.status).busy;
  useEffect(() => {
    if (!busy) return undefined;
    const timer = setInterval(load, POLL_MS);
    return () => clearInterval(timer);
  }, [busy, load]);

  const update = async (changes) => setMeeting(await apiService.patch(`/meetings/${meetingId}`, changes));
  const resummarize = async () => setMeeting(await apiService.post(`/meetings/${meetingId}/summarize`, {}));
  const rename = (speaker, name, employeeId) => update({
    speaker_names: { ...(meeting.speaker_names || {}), [speaker]: name },
    ...(employeeId !== undefined ? { speaker_employees: { [speaker]: employeeId } } : {}),
  });

  if (error) return <div className="p-6 text-sm text-rose-600">{error}</div>;
  if (!meeting) return <div className="flex items-center gap-2 p-6 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin" />Chargement…</div>;

  const status = statusOf(meeting.status);
  const summary = meeting.summary_data;

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="mx-auto max-w-4xl space-y-4 px-4 py-5">
        <div className="flex flex-wrap items-center gap-3">
          <Link to={meeting.project_id ? `/project/${meeting.project_id}` : '/meetings'} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-blue-600">
            <ArrowLeft className="h-4 w-4" /> {meeting.project_id ? meeting.project_name : 'Mes réunions'}
          </Link>
          <ChangeScope meeting={meeting} onChanged={setMeeting} />
        </div>

        <header className="rounded-2xl border border-slate-100 bg-white p-4 shadow-sm">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <h1 className="text-xl font-semibold text-slate-900">{meeting.title}</h1>
              <p className="text-sm text-slate-500">
                {formatMeetingDate(meeting.started_at)}
                {meeting.duration_seconds ? ` · ${formatDuration(meeting.duration_seconds)}` : ''}
                {` · enregistrée par ${meeting.created_by_name || '—'}`}
              </p>
              <MeetingParticipants meeting={meeting} employees={employees} onSave={update} />
              {meeting.twins?.length > 0 && (
                <p className="mt-1 text-xs text-slate-500">
                  Aussi enregistrée par{' '}
                  {meeting.twins.map((twin, index) => (
                    <React.Fragment key={twin.id}>
                      {index > 0 && ', '}
                      <Link to={`/meetings/${twin.id}`} className="font-medium text-blue-600 hover:underline">{twin.created_by_name || 'un collègue'}</Link>
                    </React.Fragment>
                  ))}
                  {' '}· une seule synthèse rejoint le journal du projet.
                </p>
              )}
            </div>
            <span className={`rounded-full border px-2.5 py-1 text-xs font-semibold ${status.tone}`}>{status.label}</span>
          </div>
          {meeting.status !== 'failed' && meeting.status !== 'ready' && <div className="mt-3"><Progress status={meeting.status} /></div>}
          {meeting.status === 'recording' && user?.employee_id === meeting.created_by_employee && (
            <InterruptedRecording meeting={meeting} onSent={setMeeting} />
          )}
          {meeting.status === 'failed' && (
            <div className="mt-3 flex flex-wrap items-center gap-3 rounded-xl bg-amber-50 p-3 text-sm text-amber-900">
              <AlertTriangle className="h-4 w-4" />
              <span className="flex-1">{meeting.error || 'Le traitement a échoué.'}</span>
              {!meeting.audio_deleted && meeting.can_edit && (
                <button type="button" onClick={async () => setMeeting(await apiService.post(`/meetings/${meetingId}/retry`, {}))}
                  className="flex items-center gap-1 rounded-lg bg-amber-600 px-3 py-1.5 text-xs font-semibold text-white">
                  <RotateCcw className="h-3.5 w-3.5" /> Relancer
                </button>
              )}
            </div>
          )}
          <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-slate-400">
            <span>
              {meeting.audio_deleted ? 'Audio détruit : seul le texte est conservé.'
                : 'Audio conservé 7 jours après la réunion, puis détruit ; seul le texte reste.'}
            </span>
            {!meeting.audio_deleted && meeting.can_edit && ['ready', 'transcribed'].includes(meeting.status) && (
              <button type="button" title="Refaire la transcription depuis l'audio (puis la synthèse)"
                onClick={async () => {
                  if (window.confirm('Refaire la transcription depuis l\'audio ? La synthèse sera refaite ensuite.')) {
                    setMeeting(await apiService.post(`/meetings/${meetingId}/retry`, {}));
                  }
                }}
                className="flex items-center gap-1 hover:text-violet-600">
                <RotateCcw className="h-3 w-3" /> Refaire la transcription
              </button>
            )}
            {!meeting.audio_deleted && ['ready', 'transcribed'].includes(meeting.status) && (
              <VoiceFromMeeting meetingId={meetingId} refreshKey={JSON.stringify(meeting.speaker_sources || {})} />
            )}
          </div>
        </header>

        <section className="rounded-2xl border border-slate-100 bg-white p-4 shadow-sm">
          <h2 className="mb-3 flex items-center gap-2 text-base font-semibold text-slate-900">
            <Sparkles className="h-4 w-4 text-violet-500" /> Synthèse
          </h2>
          {summary ? (
            <div className="space-y-4">
              {summary.overview && <p className="text-sm leading-relaxed text-slate-700">{summary.overview}</p>}
              <Section icon={ListChecks} title="Points clés" items={summary.key_points} />
              <Section icon={CheckCircle2} title="Décisions" items={summary.decisions} />
              <Section icon={HelpCircle} title="Questions ouvertes" items={summary.open_questions} />
              <ConcernedProjects meeting={meeting} onChange={setMeeting} />
              <DetailedNotes notes={meeting.detailed_notes} />
              <NewProjectProposal meeting={meeting} onChange={setMeeting} />
              <MeetingActions meeting={meeting} employees={employees} onChange={setMeeting} />
              <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-slate-400">
                {meeting.summary_model && <span>Rédigée par Nexus.</span>}
                {meeting.can_edit && (
                  <button type="button" onClick={resummarize} className="flex items-center gap-1 hover:text-violet-600">
                    <RotateCcw className="h-3 w-3" /> Refaire la synthèse
                  </button>
                )}
              </div>
            </div>
          ) : (
            <div className="text-sm text-slate-500">
              {meeting.status === 'summarizing' && (
                <p className="flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin text-violet-500" />Nexus rédige la synthèse…</p>
              )}
              {meeting.status !== 'summarizing' && busy && <p>La synthèse sera rédigée après la transcription.</p>}
              {!busy && meeting.summary_error && (
                <div className="flex flex-wrap items-center gap-3 rounded-xl bg-amber-50 p-3 text-amber-900">
                  <span className="flex-1">Synthèse non aboutie : {meeting.summary_error}</span>
                  {meeting.can_edit && (
                    <button type="button" onClick={resummarize}
                      className="flex items-center gap-1 rounded-lg bg-amber-600 px-3 py-1.5 text-xs font-semibold text-white">
                      <RotateCcw className="h-3.5 w-3.5" /> Relancer la synthèse
                    </button>
                  )}
                </div>
              )}
              {!busy && !meeting.summary_error && <p>La synthèse sera rédigée dès que Nexus sera disponible.</p>}
            </div>
          )}
        </section>

        <section className="rounded-2xl border border-slate-100 bg-white p-4 shadow-sm">
          <h2 className="mb-3 flex items-baseline gap-2 text-base font-semibold text-slate-900">
            Transcript complet
            {meeting.transcript?.language && (
              <span className="text-xs font-normal text-slate-400">{LANGUAGES[meeting.transcript.language] || meeting.transcript.language}</span>
            )}
          </h2>
          {meeting.transcript_hidden ? (<>
            <p className="flex flex-wrap items-center gap-2 text-sm text-slate-500">
              <Lock className="h-4 w-4" /> Pas de transcript pour cette réunion : seule la synthèse est partagée.
              {user?.is_admin && !revealed && (
                <button type="button" aria-label="Détails"
                  onClick={async () => setRevealed(await apiService.get(`/meetings/${meetingId}?reveal=1`))}
                  className="ml-auto text-[10px] text-slate-200 hover:text-slate-400">·</button>
              )}
            </p>
            {revealed?.transcript && <div className="mt-3 opacity-80"><Transcript meeting={revealed} onRename={() => {}} /></div>}
          </>) : (<>
            {!meeting.can_read_transcript && (
              <p className="flex items-center gap-2 text-sm text-slate-500"><Lock className="h-4 w-4" />Réservé aux participants de la réunion.</p>
            )}
            {meeting.can_read_transcript && !meeting.transcript && (
              <p className="text-sm text-slate-500">{busy ? 'Transcription en cours…' : 'Aucun transcript.'}</p>
            )}
            {meeting.can_read_transcript && meeting.transcript && <Transcript meeting={meeting} onRename={rename} />}
            {meeting.can_delete && (
              <button type="button" onClick={async () => {
                if (!window.confirm('Masquer le transcript ? Il ne pourra plus être affiché : seule la synthèse restera partagée.')) return;
                setMeeting(await apiService.patch(`/meetings/${meetingId}`, { hide_transcript: true }));
              }}
                className="mt-2 text-xs text-slate-400 hover:text-slate-600">Ne plus afficher le transcript (seulement la synthèse)</button>
            )}
          </>)}
        </section>

        {meeting.can_delete && (
          <div className="flex justify-end">
            {confirmDelete ? (
              <div className="flex items-center gap-2 text-sm">
                <span className="text-slate-600">Supprimer définitivement la réunion et son transcript ?</span>
                <button type="button" onClick={async () => { await apiService.delete(`/meetings/${meetingId}`); navigate(meeting.project_id ? `/project/${meeting.project_id}` : '/meetings'); }}
                  className="rounded-lg bg-rose-600 px-3 py-1.5 text-xs font-semibold text-white">Supprimer</button>
                <button type="button" onClick={() => setConfirmDelete(false)} className="rounded-lg border px-3 py-1.5 text-xs">Annuler</button>
              </div>
            ) : (
              <button type="button" onClick={() => setConfirmDelete(true)} className="flex items-center gap-1 text-xs text-slate-400 hover:text-rose-600">
                <Trash2 className="h-3.5 w-3.5" /> Supprimer la réunion
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default MeetingDetail;
